import { useEffect, useState, useRef } from 'react';
import logger from '~/services/logger';

interface CleengTokens {
  jwt: string | null;
  refreshToken: string | null;
}

declare global {
  interface Window {
    cleeng?: {
      onAuthTokensUpdate: (callback: (tokens: CleengTokens) => void) => void;
      logout: () => void;
      setAuthTokens: (tokens: CleengTokens) => Promise<void>;
      onWidgetEvent: (
        callback: (event: { name: string; data: any }) => void
      ) => void;
    };
  }
}

interface UseCleengLowCodeProps {
  cleeng_environment: string;
  authData?: {
    access_token?: string;
    refresh_token?: string;
  };
  onWidgetEvent?: (event: { name: string; data: any }) => void;
}

interface UseCleengLowCodeReturn {
  isCleengLowCodeLoading: boolean;
}

const CLEENG_PROD_URL = 'https://widgets.prod.cleeng.com/cleeng.js';
const CLEENG_SANDBOX_URL = 'https://widgets.sandbox.cleeng.com/cleeng.js';

export function useCleengLowCode({
  cleeng_environment,
  authData,
  onWidgetEvent,
}: UseCleengLowCodeProps): UseCleengLowCodeReturn {
  const [isCleengLowCodeLoading, setIsCleengLowCodeLoading] = useState(true);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src =
      cleeng_environment === 'production'
        ? CLEENG_PROD_URL
        : CLEENG_SANDBOX_URL;

    const initializeCleeng = () => {
      const cleeng = window.cleeng;
      if (!cleeng) return false;

      if (onWidgetEvent) {
        cleeng.onWidgetEvent(onWidgetEvent);
      }

      if (authData?.access_token && authData?.refresh_token) {
        cleeng
          .setAuthTokens({
            jwt: authData.access_token,
            refreshToken: authData.refresh_token,
          })
          .catch((error: unknown) => {
            logger.error(
              `useCleengLowCode: setAuthTokens failed: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          });
      }

      setIsCleengLowCodeLoading(false);
      return true;
    };

    const handleCleengMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'CLEENG_SDK_LOADED') return;

      const isCleengInitialized = initializeCleeng();
      if (isCleengInitialized) return;

      logger.error(
        'useCleengLowCode: window.cleeng is not available after CLEENG_SDK_LOADED'
      );
      setIsCleengLowCodeLoading(false);
    };

    script.onerror = () => {
      logger.error('useCleengLowCode: failed to load Cleeng script');
      setIsCleengLowCodeLoading(false);
    };

    window.addEventListener('message', handleCleengMessage);
    document.head.appendChild(script);
    scriptRef.current = script;

    // SDK may already be loaded (e.g. browser back/forward to /payment).
    // CLEENG_SDK_LOADED will not fire again in that case.
    initializeCleeng();

    return () => {
      if (scriptRef.current && document.head.contains(scriptRef.current)) {
        document.head.removeChild(scriptRef.current);
      }
      scriptRef.current = null;
      window.removeEventListener('message', handleCleengMessage);
    };
  }, [cleeng_environment]);

  useEffect(() => {
    if (!authData?.access_token && window.cleeng) {
      window.cleeng.logout();
    }
  }, [authData?.access_token]);

  return {
    isCleengLowCodeLoading,
  };
}
